/* =====================================================================
   TPW LOADING — window.TPWLoading · "the Beam" (foundation API)
   =====================================================================
   The ONE loading language for every WORKING -> DONE state. WIDE buttons
   fill a FULL-BUTTON light wash (a contrast tint of the surface) L->R as
   they work; round buttons sweep an arc; standalone surfaces use the line
   beam. Then a checkmark draws on + the label swaps to a personal,
   success line ("Added" via TPWLoading.ownLine; R3A-12).
   Decorator only: never touches the cart fetch, the
   variant logic, or the no-JS fallback — it overlays visuals + swaps a
   label + manages aria-busy / a live region.

   ── API ────────────────────────────────────────────────────────────
   TPWLoading.start(el, opts)
       Put `el` into the working state.
       opts:
         shape   : 'wide' (default) | 'circle'  — beam geometry
         label   : string  — text to show while working (e.g. "Adding")
         onColor : 'dark'  (white beam, default) | 'light' (ink beam)
                   or any CSS colour string for a custom beam colour.
         labelEl : element  — node whose textContent is the label
                   (default: el itself, if it has no child layout)
         status  : element  — aria-live region to write (optional)
         statusText : string — text for the live region (default = label)

   TPWLoading.done(el, opts)
       Transition `el` to the success state: beam fades, tick draws on,
       quiet scale-pulse, label -> opts.label (e.g. "Added"). Holds for
       opts.hold ms (default 2200) then auto-reverts to the idle label.
       opts:
         label, labelEl, status, statusText (as above)
         hold       : ms before auto-revert (default 2200; 0 = manual)
         idleLabel  : text to restore on revert (default = captured idle)
         tick       : false to suppress the drawn tick (host has its own)

   TPWLoading.reset(el)
       Immediately clear all loading state + restore the idle label.

   TPWLoading.run(el, promise, opts)
       Convenience: start(el, opts.start) then on resolve done(el,
       opts.done) / on reject reset(el). Returns the promise.
   ── notes ──────────────────────────────────────────────────────────
   • Reduced-motion is read LIVE per call: instant label/tick swap, no
     beam, no pulse (handled here AND defensively in the CSS @media).
   • aria-busy="true" while working, removed on done/reset.
   • Idempotent: a second start() while working is a no-op (el.__tpwlBusy).
   • Standalone LINE beam (no button) is provided in CSS (.tpwl-line) for
     P2/P3/P5 reuse; this file wires the BUTTON variants only this run.
   ===================================================================== */
(function () {
  "use strict";
  if (window.TPWLoading) return;

  var TICK_SVG =
    '<svg class="tpwl-tick" viewBox="0 0 24 24" aria-hidden="true">' +
    '<path d="M5 12l4.5 4.5L19 7"/></svg>';

  function reduced() {
    try {
      return window.matchMedia("(prefers-reduced-motion:reduce)").matches;
    } catch (e) {
      return false;
    }
  }

  /* ── success copy ────────────────────────────────────────────────────
     R3A-12 (lane P, 2026-10-04; Cicero + Themis): the per-type "[thing] is yours!" set (incl. the pipe line
     "She's all yours!") is RETIRED. ONE A1 word for every product type: "Added". ownLine(type) keeps its
     signature so pdpv9.js / tpw-card-v3.js callers are unchanged; the argument is ignored. */
  var DONE_LINE = "Added";
  function ownLine() {
    return DONE_LINE;
  }

  function beamColor(onColor) {
    if (!onColor || onColor === "dark") return "#FFFFFF";
    if (onColor === "light") return "#171717";
    return onColor; // custom CSS colour
  }

  function writeStatus(opts, fallback) {
    var s = opts && opts.status;
    if (s) s.textContent = (opts.statusText != null ? opts.statusText : fallback) || "";
  }

  function setLabel(el, opts, text) {
    if (text == null) return;
    var lbl = (opts && opts.labelEl) || el.__tpwlLabelEl || el;
    if (lbl === el) {
      // Writing to the host itself: do NOT use textContent (it would wipe the
      // overlay layers — wash/ring/tick — that live as child nodes of el).
      // Update the FIRST text node instead, preserving the layers. Create one
      // if the element had no text node (icon-only buttons keep their layers).
      var tn = null, n;
      for (n = el.firstChild; n; n = n.nextSibling) {
        if (n.nodeType === 3) { tn = n; break; } // first text node
      }
      if (tn) tn.nodeValue = text;
      else el.insertBefore(document.createTextNode(text), el.firstChild);
    } else {
      lbl.textContent = text;
    }
    el.__tpwlManagedLabel = true; // a label was driven -> safe to restore on reset
  }

  /* lazily build + cache the beam/tick/ring overlay layers on the host */
  function ensureLayers(el, shape) {
    el.classList.add("tpwl-host");
    if (!el.__tpwlLayers) {
      el.__tpwlLayers = {};
      // capture the idle label text once (only meaningful when label === el text)
      el.__tpwlIdleLabel = el.textContent.trim();
    }
    var L = el.__tpwlLayers;

    if (shape === "circle") {
      if (!L.ring) {
        var size = Math.min(el.clientWidth || 36, el.clientHeight || 36) || 36;
        var r = (size - 4) / 2; // inset for the 2px stroke
        var circ = 2 * Math.PI * r;
        var svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        svg.setAttribute("class", "tpwl-ring");
        svg.setAttribute("viewBox", "0 0 " + size + " " + size);
        var c = document.createElementNS("http://www.w3.org/2000/svg", "circle");
        c.setAttribute("cx", size / 2);
        c.setAttribute("cy", size / 2);
        c.setAttribute("r", r);
        c.setAttribute("stroke-dasharray", circ);
        c.style.setProperty("--tpwl-circ", circ);
        svg.style.setProperty("--tpwl-circ", circ);
        svg.appendChild(c);
        el.appendChild(svg);
        L.ring = svg;
      }
    } else {
      // WIDE buttons now use a FULL-BUTTON light wash (full height, fills L->R)
      // instead of the old 2px base hairline. Same fill timing/easing; the button
      // keeps its own colour (this is a wash, NOT a colour flip).
      if (!L.wash) {
        var w = document.createElement("span");
        w.className = "tpwl-wash";
        el.appendChild(w);
        L.wash = w;
      }
    }
    return L;
  }

  function ensureTick(el) {
    var L = el.__tpwlLayers || (el.__tpwlLayers = {});
    if (!L.tick) {
      var holder = document.createElement("span");
      holder.innerHTML = TICK_SVG;
      var tick = holder.firstChild;
      el.appendChild(tick);
      L.tick = tick;
    }
    return L.tick;
  }

  var TPWLoading = {
    start: function (el, opts) {
      if (!el || el.__tpwlBusy) return;
      opts = opts || {};
      el.__tpwlBusy = true;
      el.__tpwlOpts = opts;
      if (opts.labelEl) el.__tpwlLabelEl = opts.labelEl;

      var shape = opts.shape === "circle" ? "circle" : "wide";
      el.__tpwlShape = shape;
      el.style.setProperty("--tpwl-beam", beamColor(opts.onColor));

      el.setAttribute("aria-busy", "true");

      // capture idle label BEFORE we overwrite it (when label drives el text)
      ensureLayers(el, shape);
      if (opts.idleLabel == null && (!opts.labelEl || opts.labelEl === el)) {
        // refresh idle capture in case the live variant changed it (e.g. Sold out)
        // only when the label IS the element's own text content
      }

      // clear any prior done/pulse state, then enter working
      el.classList.remove("tpwl-is-done", "tpwl-pulse");
      // force reflow so re-entering the working class restarts the fill animation
      void el.offsetWidth;
      el.classList.add("tpwl-is-working");

      setLabel(el, opts, opts.label);
      writeStatus(opts, opts.label);
    },

    done: function (el, opts) {
      if (!el) return;
      opts = opts || {};
      var startOpts = el.__tpwlOpts || {};
      // inherit labelEl/status from start() if not re-passed
      var merged = {
        labelEl: opts.labelEl || startOpts.labelEl,
        status: opts.status || startOpts.status,
      };
      if (merged.labelEl) el.__tpwlLabelEl = merged.labelEl;

      el.classList.remove("tpwl-is-working");
      el.setAttribute("aria-busy", "false");
      el.removeAttribute("aria-busy");

      var rm = reduced();
      var wantTick = opts.tick !== false;

      if (wantTick && !rm) ensureTick(el);
      else if (wantTick && rm) {
        // reduced-motion: tick appears instantly (no draw-on)
        var t = ensureTick(el);
        if (t) {
          t.style.opacity = "1";
          t.style.transform = "translate(-50%,-50%) scale(1)";
        }
      }

      el.classList.add("tpwl-is-done");
      if (!rm) el.classList.add("tpwl-pulse");

      setLabel(el, { labelEl: merged.labelEl }, opts.label);
      writeStatus(merged, opts.statusText != null ? opts.statusText : opts.label);

      el.__tpwlBusy = false;

      var hold = opts.hold != null ? opts.hold : 2200;
      var idle = opts.idleLabel != null ? opts.idleLabel : el.__tpwlIdleLabel;
      if (el.__tpwlTimer) clearTimeout(el.__tpwlTimer);
      if (hold > 0) {
        el.__tpwlTimer = setTimeout(function () {
          TPWLoading.reset(el, idle);
        }, hold);
      }
    },

    reset: function (el, idleLabel) {
      if (!el) return;
      if (el.__tpwlTimer) {
        clearTimeout(el.__tpwlTimer);
        el.__tpwlTimer = null;
      }
      el.classList.remove("tpwl-is-working", "tpwl-is-done", "tpwl-pulse");
      el.removeAttribute("aria-busy");
      el.__tpwlBusy = false;
      var L = el.__tpwlLayers;
      if (L && L.tick) {
        L.tick.style.opacity = "";
        L.tick.style.transform = "";
        var p = L.tick.querySelector("path");
        if (p) p.style.strokeDashoffset = ""; // let CSS re-arm for next draw
      }
      // ONLY restore label text if a label was actually driven by start()/done()
      // (icon-only buttons, e.g. the legacy ATC, never had their text managed —
      // overwriting el.textContent would wipe their inner icon/spinner markup).
      if (el.__tpwlManagedLabel) {
        var label = idleLabel != null ? idleLabel : el.__tpwlIdleLabel;
        var lbl = el.__tpwlLabelEl || el;
        // same layer-preserving write as setLabel() when restoring on the host
        if (label != null) setLabel(el, { labelEl: lbl }, label);
      }
    },

    run: function (el, promise, opts) {
      opts = opts || {};
      this.start(el, opts.start || {});
      var self = this;
      return Promise.resolve(promise).then(
        function (val) {
          self.done(el, opts.done || {});
          return val;
        },
        function (err) {
          self.reset(el, opts.idleLabel);
          throw err;
        }
      );
    },
  };

  // expose the product-aware success-copy resolver so call sites can map a
  // success line: one word, "Added" (R3A-12).
  TPWLoading.ownLine = ownLine;

  window.TPWLoading = TPWLoading;
})();
